import { IonIcon, useIonRouter } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { documentsOutline } from "ionicons/icons";
import { InfoModal } from "@/components/molecules/info-modal";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { DeclarationPreviewModal } from "@/components/molecules/shift/declaration-preview-modal";
import { HoursFormModal } from "@/components/molecules/shift/hours-form-modal";
import { HoursPreviewModal } from "@/components/molecules/shift/hours-preview-modal";
import { TravelExpensesFormModal } from "@/components/molecules/shift/travel-expenses-form-modal";
import { type Shift } from "@/lib/backend/api";
import {
  useDeclarationInfoSections,
  useHoursInfoSections,
  useTravelExpensesInfoSections,
} from "@/lib/constants/application-info";
import { useCompletedShiftActions } from "@/lib/hooks/use-completed-shift-actions";
import { useShiftDisplayTimeDetailed } from "@/lib/hooks/use-shift-display-time";
import { timeIcon } from "@/lib/icons/time.icon";
import { Routes } from "@/lib/router";
import { opensHoursPreview } from "@/lib/shift/employee-hours-response";
import { formatInTz } from "@/utils/format-datetime";

interface CompletedShiftCardOverviewProps {
  shift: Shift;
}

export function CompletedShiftCardOverview({ shift }: CompletedShiftCardOverviewProps) {
  const { t } = useTranslation();
  const declarationInfoSections = useDeclarationInfoSections();
  const hoursInfoSections = useHoursInfoSections();
  const travelExpensesInfoSections = useTravelExpensesInfoSections();
  const router = useIonRouter();
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursInfoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursPreviewModalRef = useRef<HTMLIonModalElement>(null);
  const travelExpensesInfoModalRef = useRef<HTMLIonModalElement>(null);

  // Calculate display time - shows actual worked hours if available
  const displayTime = useShiftDisplayTimeDetailed(shift);

  // All business logic is handled by the state machine hook
  const {
    permissions,
    hourButtonState,
    declarationButtonState,
    expenseClaim,
    expenseClaimFileUrl,
    isLoadingExpenseClaim,
    isErrorExpenseClaim,
    formHours,
    FormElementHours,
    employeeResponseMutation,
    formTravelExpenses,
    FormElementExpenses,
    validator,
    ClockInWebScanner,
    ClockOutWebScanner,
    openHoursModal,
    openExpensesModal,
    openDeclarationPreview,
    closeModal,
    modalHoursRef,
    modalExpensesRef,
    modalDeclarationPreviewRef,
  } = useCompletedShiftActions(shift);

  // Parse dates for display — in projecttijd. `getDate()` en `toLocaleDateString()` lazen allebei
  // de klok van het toestel: op een telefoon westelijk van Greenwich stond er de dag ervóór boven
  // een dienst.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;
  const day = formatInTz(shift.startAt, { day: "numeric", timeZone });
  const month = formatInTz(shift.startAt, { month: "short", timeZone });
  const weekday = formatInTz(shift.startAt, { weekday: "short", timeZone });

  // Event handlers
  const handleCardClick = () => {
    if (shift.projectId) {
      router.push(Routes.Project({ id: shift.projectId }), "forward");
    }
  };

  const handleHoursClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (opensHoursPreview(shift.shiftTimeEntry, displayTime.isLocked)) {
      // Read-only zodra de uren vastliggen, en ook zodra het kantoor ze invoerde: dan corrigeert
      // de medewerker ze niet maar reageert hij erop, en die reactie zit hier. Eigen invoer blijft
      // tot de goedkeuring bewerkbaar; ingediend hebben is geen reden om er niet meer bij te
      // mogen (#495).
      hoursPreviewModalRef.current?.present();
    } else if (hourButtonState.action) {
      // QR code action
      hourButtonState.action();
    } else {
      // Manual entry modal
      openHoursModal();
    }
  };

  const handleDeclarationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (expenseClaim) {
      // Show preview if expense claim exists
      openDeclarationPreview();
    } else {
      // ANDROID FIX: Reset form BEFORE opening modal to clear stale data
      // This ensures fresh state on each submission attempt
      // Fixes issue where first upload fails but second succeeds
      formTravelExpenses.reset();
      // Show form if no expense claim
      openExpensesModal();
    }
  };

  // Get status color for hours
  const getHoursStatusColor = () => {
    if (!shift.shiftTimeEntry) {
      return "";
    }
    switch (shift.shiftTimeEntry.status) {
      case "ACCEPTED":
        return "border-success-100 bg-success-100/10";
      case "REJECTED":
        return "border-error-100 bg-error-100/10";
      case "PENDING":
      default:
        return "border-warning-100 bg-warning-100/10";
    }
  };

  const getHoursTextColor = () => {
    if (!shift.shiftTimeEntry) {
      return "text-txt-primary";
    }
    switch (shift.shiftTimeEntry.status) {
      case "ACCEPTED":
        return "text-success-120";
      case "REJECTED":
        return "text-error-120";
      case "PENDING":
      default:
        return "text-warning-120";
    }
  };

  // Get status color for declaration
  const getDeclarationStatusColor = () => {
    if (!expenseClaim) {
      return "";
    }
    switch (expenseClaim.status) {
      case "ACCEPTED":
        return "border-success-100 bg-success-100/10";
      case "REJECTED":
        return "border-error-100 bg-error-100/10";
      case "PENDING":
      default:
        return "border-warning-100 bg-warning-100/10";
    }
  };

  const getDeclarationTextColor = () => {
    if (!expenseClaim) {
      return "text-txt-primary";
    }
    switch (expenseClaim.status) {
      case "ACCEPTED":
        return "text-success-120";
      case "REJECTED":
        return "text-error-120";
      case "PENDING":
      default:
        return "text-warning-120";
    }
  };

  return (
    <>
      <div onClick={handleCardClick} className="flex w-full cursor-pointer items-start gap-5">
        {/* Date indicator */}
        <div className="flex shrink-0 flex-col items-center gap-1 pt-2.5 capitalize">
          <div className="flex flex-col items-center pb-0.5 text-center text-txt-primary">
            <p className="text-sm font-semibold leading-[1.4]">{month}</p>
            <p className="text-2xl font-semibold leading-[1.2]">{day}</p>
          </div>
          <p className="text-sm font-medium leading-[1.4] text-txt-tertiary">{weekday}</p>
        </div>

        {/* Shift details */}
        <div className="flex flex-1 items-start gap-5 border-b border-solid border-bordercolor-primary">
          <div className="flex flex-1 flex-col gap-4 pb-5">
            {/* Project image */}
            <ProjectImage
              projectId={shift.projectId}
              imageKey={shift.project?.image}
              alt={shift.project?.name || "Project"}
              className="aspect-video w-full rounded-xl"
            />

            {/* Project name and time */}
            <div className="flex flex-col gap-1">
              <h3 className="text-17 font-semibold leading-[1.4] text-txt-primary">
                {shift.project?.name}
              </h3>

              {/* Time display - compact format */}
              <div className="flex items-center gap-1.5 text-txt-secondary">
                <span className="text-md font-medium leading-[1.5]">
                  {displayTime.startTime} - {displayTime.endTime}
                  {displayTime.isFullyDeclared && displayTime.durationDutch && (
                    <span className="text-txt-tertiary"> ({displayTime.durationDutch})</span>
                  )}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2.5">
              {/* Hours button */}
              {hourButtonState.isVisible && (
                <button
                  type="button"
                  onClick={handleHoursClick}
                  disabled={hourButtonState.isDisabled}
                  // Zonder invoerrecht opent deze knop alleen de preview (admin#1093), dus draagt hij
                  // de status en niet de primaire kleur die om invoer vraagt.
                  className={`flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid px-4 py-3 transition-colors disabled:opacity-50 ${
                    displayTime.isFullyDeclared || hourButtonState.isReadOnly
                      ? getHoursStatusColor()
                      : "border-button-primary bg-button-primary"
                  }`}
                >
                  <p
                    className={`text-md font-semibold leading-[1.5] tracking-[0.08px] ${
                      displayTime.isFullyDeclared || hourButtonState.isReadOnly
                        ? getHoursTextColor()
                        : "text-txt-inverted"
                    }`}
                  >
                    {/* QR projects: show the live clock action ("Uitklokken"/"Inklokken")
                        so an employee clocking out after the scheduled end time (shift now
                        in the Completed tab) recognises the button. Manual entry keeps "Uren". */}
                    {permissions.enableQRCodeClocking
                      ? hourButtonState.text
                      : t("shift:hours.title")}
                  </p>
                  <IonIcon
                    icon={timeIcon}
                    className={`size-4 ${
                      displayTime.isFullyDeclared || hourButtonState.isReadOnly
                        ? getHoursTextColor()
                        : "text-txt-inverted"
                    }`}
                  />
                </button>
              )}

              {/* Declaration button - independent of hours */}
              {declarationButtonState.isVisible && (
                <button
                  type="button"
                  onClick={handleDeclarationClick}
                  disabled={declarationButtonState.isDisabled}
                  className={`flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid px-4 py-3 transition-colors disabled:opacity-50 ${
                    expenseClaim
                      ? getDeclarationStatusColor()
                      : "border-bordercolor-primary bg-transparent"
                  }`}
                >
                  <p
                    className={`text-md font-semibold leading-[1.5] tracking-[0.08px] ${
                      expenseClaim ? getDeclarationTextColor() : "text-txt-primary"
                    }`}
                  >
                    {t("shift:declaration.title")}
                  </p>
                  <IonIcon
                    icon={documentsOutline}
                    className={`size-4 ${expenseClaim ? getDeclarationTextColor() : "text-txt-primary"}`}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Hours Modal - Only for manual entry (not QR) */}
      <HoursFormModal
        modalRef={modalHoursRef}
        hoursInfoModalRef={hoursInfoModalRef}
        formHours={formHours}
        FormElementHours={FormElementHours}
        validator={validator}
        closeModal={closeModal}
      />
      {/* Travel Expenses Modal */}
      <TravelExpensesFormModal
        modalRef={modalExpensesRef}
        travelExpensesInfoModalRef={travelExpensesInfoModalRef}
        formTravelExpenses={formTravelExpenses}
        FormElementExpenses={FormElementExpenses}
        closeModal={() => {
          formTravelExpenses.reset();
          closeModal();
        }}
      />
      {/* Declaration Preview Modal */}
      <DeclarationPreviewModal
        modalRef={modalDeclarationPreviewRef}
        infoModalRef={infoModalRef}
        expenseClaim={expenseClaim}
        expenseClaimFileUrl={expenseClaimFileUrl}
        isLoadingExpenseClaim={isLoadingExpenseClaim}
        isErrorExpenseClaim={isErrorExpenseClaim}
        closeModal={closeModal}
      />
      {/* Hours Preview Modal */}
      <HoursPreviewModal
        modalRef={hoursPreviewModalRef}
        hoursInfoModalRef={hoursInfoModalRef}
        shift={shift}
        durationDutch={displayTime.durationDutch}
        onRespond={(response, note) => employeeResponseMutation.mutate({ response, note })}
        isRespondingToHours={employeeResponseMutation.isPending}
      />
      {/* Declaration Info Modal */}
      <InfoModal
        modalRef={infoModalRef}
        title={t("shift:declaration.info.title")}
        sections={declarationInfoSections}
      />
      {/* Hours Info Modal */}
      <InfoModal
        modalRef={hoursInfoModalRef}
        title={t("shift:hours.info.title")}
        sections={hoursInfoSections}
      />
      {/* Travel Expenses Info Modal */}
      <InfoModal
        modalRef={travelExpensesInfoModalRef}
        title="Informatie over reiskosten"
        sections={travelExpensesInfoSections}
      />
      {/* QR Scanners */}
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}
    </>
  );
}
